<template>
	<div
		class="app-time-picker"
		v-on-clickaway="closePicker"
	>
		<div
			v-if="$slots.default"
			class="app-time-picker__label"
		>
			<slot></slot>
		</div>

		<div class="app-time-picker__wrapper">
			<div
				class="app-time-picker__toggle"
				:class="{
					'app-time-picker__toggle--disabled' : $attrs.disabled,
					'app-time-picker__toggle--error' : hasError
				}"
				@click="open = !open"
			>
				<input
					type="text"
					class="app-time-picker__input"
					v-bind="$attrs"
					@invalid="setInvalidMessage"
					:value="displayValue"
				>

				<AppIcon
					class="app-time-picker__icon"
					icon-name="clock"
					icon-color="#778395"
				/>
			</div>

			<div
				v-if="open"
				class="app-time-picker__picker"
				:class="{'app-time-picker__picker--top' : getPositionReference().isTop,
						'app-time-picker__picker--right' : getPositionReference().isRight}"
			>
				<AppTimePicker
					:value="from"
					:min="min"
					:max="max"
					@input="updateValue({from: $event})"
				>
					From
				</AppTimePicker>

				<AppTimePicker
					:value="to"
					:min="from ? from : null"
					:max="max"
					@input="updateValue({to: $event})"
				>
					To
				</AppTimePicker>
			</div>
		</div>

		<p
			v-if="hasError"
			class="app-time-picker__error"
		>
			{{ error || invalidMessage }}
		</p>
	</div>
</template>

<script>

export default {
	inheritAttrs: false,

	props: {
		value: {
			type: Array,
			validator ([from, to]) {
				const pattern = new RegExp(/^(?:0?[0-9]|1[0-2])[-:][0-5][0-9]\s*[AP]M|[ap]m$/)
				if (from && !pattern.test(from)) return false
				if (to && !pattern.test(to)) return false
				return true
			}
		},
		min: String,
		max: String,
		error: String
	},

	data () {
		return {
			timeValue: null,
			open: false,
			invalidMessage: null,
			from: null,
			to: null
		}
	},

	watch: {
		from (val) {
			if (val && this.to) {
				const today = new Date().toDateString()
				const timeFrom = new Date(`${today} ${val}`).getTime()
				const timeTo = new Date(`${today} ${this.to}`).getTime()
				if (timeFrom > timeTo) {
					this.to = val
					this.updateValue()
				}
			}
		},

		value: {
			handler	(val) {
				if (val && val.length) {
					this.from = val[0] || null
					this.to = val[1] || null
				} else {
					this.from = null
					this.to = null
				}
			},

			immediate: true
		}
	},

	computed: {
		hasError () {
			return this.error || this.invalidMessage
		},

		displayValue () {
			if (!this.value || !this.value.length) return ''
			const timeFrom = this.value[0] || ''
			const timeTo = this.value[1] || ''
			return `${timeFrom.toUpperCase()} - ${timeTo.toUpperCase()}`
		}
	},

	methods: {
		updateValue (newVal = {}) {
			this.invalidMessage = null
			if (Object.prototype.hasOwnProperty.call(newVal, 'from')) this.from = newVal['from']
			if (Object.prototype.hasOwnProperty.call(newVal, 'to')) this.to = newVal['to']
			if (!this.from && !this.to) return this.$emit('input', null)
			this.$emit('input', [this.from, this.to])
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = 'Fill up this field'
			} else {
				this.invalidMessage = event.target.validationMessage
			}
		},

		getPositionReference () {
			const elementPosition = this.$el.getBoundingClientRect()
			const isTop = window.innerHeight - elementPosition.top < elementPosition.top
			const isRight = window.innerWidth - elementPosition.left < elementPosition.left
			return { isTop, isRight }
		},

		closePicker () {
			this.open = false
		}
	}
}
</script>

<style lang="scss" scoped>
.app-time-picker {
	&__label {
		display: inline-block;
		font-size: 1em;
		margin-bottom: .5em;
	}

	&__wrapper {
		margin: 0;
		padding: 0;
		position: relative;
		color: $default;
	}

	&__toggle {
		font-size: 1em;
		border-radius: 3px;
		padding: 5px 8px;
		border: 1px solid $default-light;
		display: flex;
		align-items: center;
		cursor: pointer;

		&--disabled {
			pointer-events: none;
			opacity: 0.5;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__input {
		flex: 1 1 auto;
		font-size: 1em;
		line-height: 1em;
		margin-right: 8px;
		border: none;
		outline: none;
		color: $default;
		pointer-events: none;
		background-color: #fff;

		&::placeholder {
			color: $default-light;
		}
	}

	&__icon {
		width: 1em;
	}

	&:hover &__toggle {
		border-color: $primary;

		&--disabled {
			border-color: $default-light;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__picker {
		position: absolute;
		z-index: 999;
		top: 100%;
		left: 0;
		margin: 5px 0;
		box-shadow: 0 0 3px $default-light;
		background-color: #fff;
		padding: 10px;

		&--top {
			top: auto;
			bottom: 100%;
		}

		&--right {
			left: auto;
			right: 0;
		}

		> * {
			display: block;
			margin: .5em 0;
		}
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>
